import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setDeviceUniqueID,
    setName,
    setHospitalID,
    setHospitalName,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setEmail,
    setMobileNumber,
    setPassword,
    setStateList,
    setCountryList,
} from '@store/reducers/auth/signupSlice';
import Select, { ActionMeta } from 'react-select';
import { useTranslation } from 'react-i18next';
import { MdInfo } from 'react-icons/md';
import { fetchCountries, fetchCountryId, fetchStates } from '@utils/utils';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import {
    MOBILE_NUMBER_PATTERN,
    MOBILE_NUMBER_TITLE,
    PASSWORD_MIN_Length,
    PASSWORD_MAX_Length,
    PASSWORD_PATTERN,
    PASSWORD_TITLE,
    PINCODE_MAXIMUM_LENGTH,
    PINCODE_MINIMUM_LENGTH,
    PINCODE_PATTERN,
    PINCODE_TITLE,
    MOBILE_NUMBER_MIN_LENGTH,
    MOBILE_NUMBER_MAX_LENGTH,
    NAME_TEXT_MIN_LENGTH,
    NAME_TEXT_MAX_LENGTH,
    NAME_TEXT_PATTERN,
    NAME_TEXT_TITLE,
    HOSPITAL_NAME_TEXT_TITLE,
    DEVICE_NUMBER_MIN_LENGTH,
    DEVICE_NUMBER_MAX_LENGTH,
    DEVICE_NUMBER_TITLE,
    DEVICE_NUMBER_PATTERN,
    HOSPITAL_NAME_PATTERN,
    ADDRESS_MAX_LENGTH,
    EMAIL_PATTERN,
    EMAIL_MAX_Length,
    EMAIL_TITLE,
} from '@constants/form';
import { getHospitalList, hospitalList } from '@utils/authUtils';
import ErrorMessage from '@components/Error/ErrorMessage';

interface OptionType {
    value: string;
    label: string; // Add hospital_id to OptionType
}

interface HospitalData {
    uuid: string;
    hospital_name: string;
    hospital_id: string;
}

const NurseForm = () => {
    // const [countryList, setCountryList] = useState<
    //     { id: string; code: string; name: string }[]
    // >([]);
    // const [stateList, setStateList] = useState<{ id: string; name: string }[]>(
    //     []
    // );
    const countryList = useAppSelector((state) => state.signup.countryList);
    const stateList = useAppSelector((state) => state.signup.stateList);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const deviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const name = useAppSelector((state) => state.signup.name);
    const hospitalID = useAppSelector((state) => state.signup.hospital_id);
    const hospitalName = useAppSelector((state) => state.signup.hospital_name);
    const address_1 = useAppSelector((state) => state.signup.address_one);
    const address_2 = useAppSelector((state) => state.signup.address_two);
    const city = useAppSelector((state) => state.signup.city);
    const state = useAppSelector((state) => state.signup.userState);
    const country = useAppSelector((state) => state.signup.country);
    const pincode = useAppSelector((state) => state.signup.pincode);
    const email = useAppSelector((state) => state.signup.email);
    const mobileNumber = useAppSelector((state) => state.signup.mobileNumber);
    const password = useAppSelector((state) => state.signup.password);
    const adminCreate = useAppSelector(
        (state) => state.signup.adminCreateNurse
    );
    const visible = useAppSelector((state) => state.signup.showPassword);
    const [error, setError] = useState<string | null>(null);
    // const [selectedOption, setSelectedOption] = useState<OptionType | null>(
    //     null
    // );
    const [hospitalIDOptions, setHospitalIDOptions] = useState<OptionType[]>(
        []
    );
    const [hospitalNameOptions, setHospitalNameOptions] = useState<
        OptionType[]
    >([]);
    const [selectedHospitalID, setSelectedHospitalID] =
        useState<OptionType | null>(null);
    const [selectedHospitalName, setSelectedHospitalName] =
        useState<OptionType | null>(null);
    const hospitalAddNurse = useAppSelector(
        (state) => state.signup.hospitalAddNurse
    );

    useEffect(() => {
        // Only fetch if country list is empty
        if (countryList.length === 0) {
            handleFetchCountryList();
        }
    }, []);

    // Fetch states on load if country has a value (for edit pages, not signup)
    useEffect(() => {
        const fetchStatesForCountry = async () => {
            if (country && countryList.length > 0 && stateList.length === 0) {
                const selectedCountry = countryList.find(
                    (c) => c.name === country
                );
                if (selectedCountry) {
                    await handleFetchStates(selectedCountry.code);
                }
            }
        };
        fetchStatesForCountry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, countryList]);

    useEffect(() => {
        if (adminCreate) {
            fetchData();
        }
    }, [adminCreate]);

    const handleFetchCountryList = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            console.error('Error fetching countries:', list.error);
        }
    };

    const fetchData = async () => {
        try {
            const list = await getHospitalList();

            const idOptions: OptionType[] = list.map((item: HospitalData) => ({
                value: item.uuid,
                label: item.hospital_id.toUpperCase(),
            }));
            const nameOptions: OptionType[] = list.map(
                (item: HospitalData) => ({
                    value: item.uuid,
                    label: item.hospital_name,
                })
            );
            setHospitalIDOptions(idOptions);
            setHospitalNameOptions(nameOptions);
            if (hospitalID) {
                const selectedOption = idOptions.find(
                    (option) =>
                        option.label.toLowerCase() === hospitalID.toLowerCase()
                );
                if (selectedOption) {
                    setSelectedHospitalID(selectedOption);
                    setSelectedHospitalName(
                        nameOptions.find(
                            (option) =>
                                option.value.toLowerCase() ===
                                selectedOption.value.toLowerCase()
                        ) || null
                    );
                    // console.log('selectedOption', selectedOption);
                }
            } else if (hospitalName) {
                const selectedOption = nameOptions.find(
                    (option) =>
                        option.label.toLowerCase() ===
                        hospitalName.toLowerCase()
                );
                if (selectedOption) {
                    setSelectedHospitalName(selectedOption);
                    setSelectedHospitalID(
                        idOptions.find(
                            (option) =>
                                option.value.toLowerCase() ===
                                selectedOption.value.toLowerCase()
                        ) || null
                    );
                }
            }
        } catch (error: any) {
            let errorMessage;

            switch (error.name) {
                case 'ConditionalCheckFailedException':
                    errorMessage = 'Condition check failed.';
                    break;
                case 'ResourceNotFoundException':
                    errorMessage = 'The specified table does not exist.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage = 'The request rate is too high.';
                    break;
                case 'ValidationException':
                    errorMessage = 'Invalid input parameters.';
                    break;
                case 'InternalServerError':
                    errorMessage = 'An internal server error occurred.';
                    break;
                case 'AccessDeniedException':
                    errorMessage = 'Access denied to the requested resource.';
                    break;
                default:
                    errorMessage = 'An unknown error occurred.';
                    break;
            }

            setError(errorMessage);
        }
    };

    const handleHospitalIDChange = (newValue: OptionType | null) => {
        if (newValue) {
            const selectedOption = hospitalNameOptions.find(
                (option) => option.value === newValue.value
            );
            if (selectedOption) {
                setSelectedHospitalID(newValue);
                setSelectedHospitalName(selectedOption);
                dispatch(setHospitalID(newValue.label.toLowerCase()));
                dispatch(setHospitalName(selectedOption.label));
            }
        } else {
            setSelectedHospitalID(null);
            setSelectedHospitalName(null);
            dispatch(setHospitalID(''));
            dispatch(setHospitalName(''));
        }
    };

    const handleHospitalNameChange = (newValue: OptionType | null) => {
        if (newValue) {
            const selectedOption = hospitalIDOptions.find(
                (option) => option.value === newValue.value
            );
            if (selectedOption) {
                setSelectedHospitalName(newValue);
                setSelectedHospitalID(selectedOption);
                dispatch(setHospitalID(selectedOption.label.toLowerCase()));
                dispatch(setHospitalName(newValue.label));
            }
        } else {
            setSelectedHospitalName(null);
            setSelectedHospitalID(null);
            dispatch(setHospitalID(''));
            dispatch(setHospitalName(''));
        }
    };

    const handleFetchStates = async (countryId: string) => {
        const result = await fetchStates(countryId);
        if (result.states) {
            dispatch(setStateList(result.states));
        } else if (result.error) {
            // Handle error message
            console.error('Error fetching states:', result.error);
        }
    };

    const handleDeviceUniqueIDChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // dispatch(setDeviceUniqueID(event.target.value));
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            dispatch(setDeviceUniqueID(value));
        }
    };

    const handleHospitalID = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setHospitalID(event.target.value));
    };

    // const hospitalOptions: OptionType[] = [
    //     { value: 'hospital_1', label: 'Hospital 1' },
    //     { value: 'hospital_2', label: 'Hospital 2' },
    //     { value: 'hospital_3', label: 'Hospital 3' },
    // ];

    // const handleHospitalName = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     dispatch(setHospitalName(event.target.value));
    // };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // dispatch(setName(event.target.value));
        const value = event.target.value;
        const regex = /^[A-Za-z\s]*$/;
        if (regex.test(value)) {
            dispatch(setName(value));
        }
    };

    // const handleHospitalID = (
    //     newValue: OptionType | null,
    //     actionMeta: ActionMeta<OptionType>
    // ) => {
    //     setSelectedOption(newValue);
    //     dispatch(setHospitalID(newValue ? newValue.value : ''));
    // };

    // const handleHospitalName = (
    //     newValue: OptionType | null,
    //     actionMeta: ActionMeta<OptionType>
    // ) => {
    //     setSelectedOption(newValue);
    //     dispatch(setHospitalName(newValue ? newValue.value : ''));
    // };

    const handleAddressOneChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressOne(event.target.value));
    };

    const handleAddressTwoChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressTwo(event.target.value));
    };

    // const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     dispatch(setCity(event.target.value));
    // };
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // Only allow alphabetic characters (letters) using regex
        const sanitizedValue = inputValue.replace(/[^A-Za-z]/g, '');
        dispatch(setCity(sanitizedValue));
    };

    const handleStateChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        dispatch(setUserState(event.target.value));
    };

    const handleCountryChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const selectedCountry = countryList.find(
            (c) => c.name === event.target.value
        );
        if (selectedCountry) {
            dispatch(setCountry(selectedCountry.name));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
            handleFetchStates(selectedCountry.code); // Fetch states for the selected country
        } else {
            dispatch(setCountry(event.target.value));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
        }
    };

    const handlePincodeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value) && value.length <= PINCODE_MAXIMUM_LENGTH) {
            dispatch(setPincode(value));
        }
        // dispatch(setPincode(event.target.value));
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setEmail(event.target.value));
    };

    const handleMobileNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value) && value.length <= MOBILE_NUMBER_MAX_LENGTH) {
            dispatch(setMobileNumber(value));
        }
        // dispatch(setMobileNumber(event.target.value));
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setPassword(event.target.value));
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    return (
        <>
            {error && <ErrorMessage message={error} />}
            <div className="flex justify-start">
                {/* <div className="w-2/4 mr-8">
                    <label htmlFor="device_unique_id" className="mb-2">
                        <div className="text-sm font-medium text-light_grey flex items-center">
                            {t('auth.signup.nurse_id')}
                            <span className="text-red ml-2">*</span>
                            <MdInfo className="text-secondary ml-2 text-base" />
                        </div>
                    </label>
                    <input
                        type="text"
                        name="device_unique_id"
                        id="device_unique_id"
                        value={deviceUniqueID}
                        onChange={handleDeviceUniqueIDChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.device_unique_id_placeholder'
                        )}
                        pattern={DEVICE_NUMBER_PATTERN}
                        title={DEVICE_NUMBER_TITLE}
                        minLength={DEVICE_NUMBER_MIN_LENGTH}
                        maxLength={DEVICE_NUMBER_MAX_LENGTH}
                        required
                    />
                </div> */}
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="name"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.name_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        id="name"
                        value={name}
                        onChange={handleNameChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.name_placeholder')}
                        minLength={NAME_TEXT_MIN_LENGTH}
                        maxLength={NAME_TEXT_MAX_LENGTH}
                        title={NAME_TEXT_TITLE}
                        pattern={NAME_TEXT_PATTERN}
                        required
                    />
                </div>
                {!hospitalAddNurse ? (
                    <div className="w-2/4">
                        {adminCreate ? (
                            <>
                                <label
                                    htmlFor="name"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.hospital_name_label')}
                                    <span className="text-red ml-2">*</span>
                                </label>
                                <Select
                                    id="hospital_name"
                                    name="hospital_name"
                                    value={selectedHospitalName}
                                    onChange={handleHospitalNameChange}
                                    options={hospitalNameOptions}
                                    placeholder={t(
                                        'auth.signup.hospital_name_placeholder'
                                    )}
                                    classNamePrefix="react-select"
                                    isSearchable
                                    required
                                />
                            </>
                        ):(
                            <>
                                <label
                                    htmlFor="hospital_id"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.hospital_id_label')}
                                    <span className="text-red ml-2">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="hospital_id"
                                    id="hospital_id"
                                    value={hospitalID}
                                    onChange={handleHospitalID}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.hospital_id_placeholder'
                                    )}
                                    required
                                />
                            </>
                        )}
                    </div>
                ):(
                    <div className="w-2/4">
                            <label
                                htmlFor="address_1"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_1_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_1"
                                id="address_1"
                                value={address_1}
                                onChange={handleAddressOneChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_1_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                    </div> 
                )}
            </div>
            {/* {(!adminCreate || visible) && ( */}
            {!hospitalAddNurse && (
                <div className="flex justify-start mt-4">
                    {/* <div className="w-2/4 mr-8">
                        {!adminCreate ? (
                            <>
                                <label
                                    htmlFor="hospital_id"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.hospital_id_label')}
                                    <span className="text-red ml-2">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="hospital_id"
                                    id="hospital_id"
                                    value={hospitalID}
                                    onChange={handleHospitalID}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.hospital_id_placeholder'
                                    )}
                                    required
                                />
                            </>
                        ) : (
                            <>
                                <label
                                    htmlFor="name"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.hospital_name_label')}
                                    <span className="text-red ml-2">*</span>
                                </label>
                                <Select
                                    id="hospital_name"
                                    name="hospital_name"
                                    value={selectedHospitalName}
                                    onChange={handleHospitalNameChange}
                                    options={hospitalNameOptions}
                                    placeholder={t(
                                        'auth.signup.hospital_name_placeholder'
                                    )}
                                    classNamePrefix="react-select"
                                    isSearchable
                                    required
                                />
                            </>
                        )}
                    </div> */}

                    {adminCreate ? (
                        <>
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="hospital_id"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.hospital_id_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <Select
                                id="hospital_id"
                                name="hospital_id"
                                value={selectedHospitalID}
                                options={hospitalIDOptions}
                                placeholder={t(
                                    'auth.signup.hospital_id_placeholder'
                                )}
                                classNamePrefix="react-select"
                                isSearchable
                                isDisabled
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="address_1"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_1_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_1"
                                id="address_1"
                                value={address_1}
                                onChange={handleAddressOneChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_1_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div>
                        </>
                    ) : (
                        <>
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="address_1"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_1_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_1"
                                id="address_1"
                                value={address_1}
                                onChange={handleAddressOneChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_1_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="address_2"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_2_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_2"
                                id="address_2"
                                value={address_2}
                                onChange={handleAddressTwoChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_2_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div>
                        </>
                    )}

                </div>
            )}
            <div className="flex justify-start mt-4">
                {adminCreate ? (
                    <>
                        {/* <div className="w-2/4 mr-8">
                            <label
                                htmlFor="address_1"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_1_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_1"
                                id="address_1"
                                value={address_1}
                                onChange={handleAddressOneChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_1_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div> */}
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="address_2"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_2_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_2"
                                id="address_2"
                                value={address_2}
                                onChange={handleAddressTwoChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_2_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="city"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.city_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                value={city}
                                onChange={handleCityChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t('auth.signup.city_placeholder')}
                                maxLength={NAME_TEXT_MAX_LENGTH}
                                required
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {/* <div className="w-2/4 mr-8">
                            <label
                                htmlFor="address_2"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.address_2_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="address_2"
                                id="address_2"
                                value={address_2}
                                onChange={handleAddressTwoChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.address_2_palceholder'
                                )}
                                maxLength={ADDRESS_MAX_LENGTH}
                                required
                            />
                        </div> */}
                        <div className="w-2/4  mr-8">
                            <label
                                htmlFor="city"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.city_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                value={city}
                                onChange={handleCityChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t('auth.signup.city_placeholder')}
                                maxLength={NAME_TEXT_MAX_LENGTH}
                                required
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="country"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.country_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="country"
                                id="country"
                                value={country}
                                onChange={handleCountryChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.country_placeholder')}
                                </option>
                                {countryList.map((country) => (
                                    <option
                                        key={country.code}
                                        value={country.name}
                                    >
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                )}
            </div>
            <div className="flex justify-start mt-4">
                {adminCreate ? (
                    <>
                        {/* <div className="w-2/4 mr-8">
                            <label
                                htmlFor="city"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.city_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                value={city}
                                onChange={handleCityChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t('auth.signup.city_placeholder')}
                                maxLength={NAME_TEXT_MAX_LENGTH}
                                required
                            />
                        </div> */}
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="country"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.country_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="country"
                                id="country"
                                value={country}
                                onChange={handleCountryChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.country_placeholder')}
                                </option>
                                {countryList.map((country) => (
                                    <option
                                        key={country.code}
                                        value={country.name}
                                    >
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="state"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.state_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="state"
                                id="state"
                                value={state}
                                onChange={handleStateChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.state_placeholder')}
                                </option>
                                {stateList.map((state) => (
                                    <option key={state.id} value={state.name}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                ) : (
                    <>
                        {/* <div className="w-2/4 mr-8">
                            <label
                                htmlFor="country"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.country_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="country"
                                id="country"
                                value={country}
                                onChange={handleCountryChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.country_placeholder')}
                                </option>
                                {countryList.map((country) => (
                                    <option
                                        key={country.code}
                                        value={country.name}
                                    >
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div> */}
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="state"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.state_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="state"
                                id="state"
                                value={state}
                                onChange={handleStateChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.state_placeholder')}
                                </option>
                                {stateList.map((state) => (
                                    <option key={state.id} value={state.name}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="pincode"
                                className=" mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.pincode_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                id="pincode"
                                value={pincode}
                                onChange={handlePincodeChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.pincode_placeholder'
                                )}
                                minLength={PINCODE_MINIMUM_LENGTH}
                                maxLength={PINCODE_MAXIMUM_LENGTH}
                                pattern={PINCODE_PATTERN}
                                title={PINCODE_TITLE}
                                required
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="flex justify-start mt-4">
                {adminCreate ? (
                    <>
                        {/* <div className="w-2/4 mr-8">
                            <label
                                htmlFor="state"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.state_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <select
                                name="state"
                                id="state"
                                value={state}
                                onChange={handleStateChange}
                                className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                required
                            >
                                <option value="" disabled>
                                    {t('auth.signup.state_placeholder')}
                                </option>
                                {stateList.map((state) => (
                                    <option key={state.id} value={state.name}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div> */}
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="pincode"
                                className=" mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.pincode_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                id="pincode"
                                value={pincode}
                                onChange={handlePincodeChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.pincode_placeholder'
                                )}
                                minLength={PINCODE_MINIMUM_LENGTH}
                                maxLength={PINCODE_MAXIMUM_LENGTH}
                                pattern={PINCODE_PATTERN}
                                title={PINCODE_TITLE}
                                required
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="email"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.email_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            {visible ? (
                                <input
                                    type="email"
                                    name="email"
                                    id="signup_email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    pattern={EMAIL_PATTERN}
                                    title={EMAIL_TITLE}
                                    maxLength={EMAIL_MAX_Length}
                                    required
                                    autoComplete="off"
                                />
                            ) : (
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    readOnly
                                    disabled
                                    autoComplete="off"
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <>
                       
                        <div className="w-2/4"></div>
                    </>
                )}
            </div>
            {adminCreate && (
                <>
                    {/* <div className="flex justify-start mt-4">
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="email"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.email_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            {visible ? (
                                <input
                                    type="email"
                                    name="email"
                                    id="signup_email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    pattern={EMAIL_PATTERN}
                                    title={EMAIL_TITLE}
                                    maxLength={EMAIL_MAX_Length}
                                    required
                                    autoComplete="off"
                                />
                            ) : (
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    readOnly
                                    disabled
                                    autoComplete="off"
                                />
                            )}
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="mobile_number"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.mobile_number_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="mobile_number"
                                id="mobile_number"
                                value={mobileNumber}
                                onChange={handleMobileNumberChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.mobile_number_placeholder'
                                )}
                                pattern={MOBILE_NUMBER_PATTERN}
                                title={MOBILE_NUMBER_TITLE}
                                minLength={MOBILE_NUMBER_MIN_LENGTH}
                                maxLength={MOBILE_NUMBER_MAX_LENGTH}
                                required
                            />
                        </div>
                    </div> */}
                    {visible && (
                        <div className="flex justify-start mt-4">
                            <div className="relative w-2/4 mr-8">
                                <label
                                    htmlFor="password"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.password_label')}
                                </label>
                                <div className="relative flex items-center rounded-md">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        id="signup_password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        className="form-control px-3 py-2 placeholder-gains_boro"
                                        placeholder={t(
                                            'auth.signup.password_placeholder'
                                        )}
                                        minLength={PASSWORD_MIN_Length}
                                        maxLength={PASSWORD_MAX_Length}
                                        pattern={PASSWORD_PATTERN}
                                        title={PASSWORD_TITLE}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 cursor-pointer"
                                        onClick={handleTogglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <IoEyeOutline className="text-secondary" />
                                        ) : (
                                            <IoEyeOffOutline className="text-secondary" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="w-2/4">
                                <label
                                    htmlFor="mobile_number"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.mobile_number_label')}
                                    <span className="text-red ml-2">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="mobile_number"
                                    id="mobile_number"
                                    value={mobileNumber}
                                    onChange={handleMobileNumberChange}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.mobile_number_placeholder'
                                    )}
                                    pattern={MOBILE_NUMBER_PATTERN}
                                    title={MOBILE_NUMBER_TITLE}
                                    minLength={MOBILE_NUMBER_MIN_LENGTH}
                                    maxLength={MOBILE_NUMBER_MAX_LENGTH}
                                    required
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default NurseForm;
